import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    TibboDataType,
} from '@appblocks/codegeneration/types';

import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import Tibbits from '@appblocks/codegeneration/Tibbits';
import { TIBBO_DEVICE_ID } from '@appblocks/platforms/PlatformUtils';

export default class Cellular extends BaseFeature {
    constructor() {
        super();
        this.name = 'Cellular';
        this.id = 'cell';
        this.icon = 'IoIosCellular';
        this.requirements = ['TPP'];
        const settings = [
            {
                id: 'ENABLED',
                name: 'Cellular Enabled',
                settingType: SettingType.OPTION,
                required: true,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
                helpText: 'If "Automatic Interface Switching" is enabled, the cellular interface will only be used if no other interface is available.',
            },
            {
                id: 'CELL_MODE',
                name: 'Mode',
                settingType: SettingType.HIDDEN,
                required: true,
                default: 'CELL__PPP_MODE_ISENABLED',
                options: [
                    {
                        name: 'PPP Mode',
                        value: 'CELL__PPP_MODE_ISENABLED',
                    },
                    {
                        name: 'AT Command Mode',
                        value: 'CELL__AT_USERMODE_ISENABLED',
                    },
                    // {
                    //     "name": "GPS Mode",
                    //     "value": "CELL__GPS_MODE_ISENABLED"
                    // }
                ],
            },
            {
                id: 'CELL_APN',
                name: 'Access Point Name',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                required: true,
                helpText: 'The APN to use for the cellular connection. Only explicitly set if you are having issues with the default discovered APN.',
                default: '',
                setting: {
                    SETTINGID: 'CELLAPN',
                    NAME: 'Cellular Access Point Name',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    DEFAULT: '',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
            },
            {
                id: 'CELL_UN',
                name: 'PPP Username',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                required: true,
                default: 'myusername@realm',
                setting: {
                    SETTINGID: 'CELLUN',
                    NAME: 'PPP Username',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 50,
                    DEFAULT: '',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
            },
            {
                id: 'CELL_PW',
                name: 'PPP Password',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                required: true,
                default: '',
                setting: {
                    SETTINGID: 'CELLPW',
                    NAME: 'PPP Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 50,
                    DEFAULT: '',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
            },
            {
                id: 'CELL_PIN',
                name: 'Sim Card Pin',
                settingType: SettingType.STRING,
                dataType: TibboDataType.SETTING,
                required: true,
                default: '?',
                setting: {
                    SETTINGID: 'CELLPIN',
                    NAME: 'SIM Card Pin',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MIN: 0,
                    MAX: 10,
                    DEFAULT: '',
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
            },
            {
                id: 'CELL_BAND_TYPE',
                name: 'Band Type',
                settingType: SettingType.OPTION,
                required: true,
                default: '1',
                helpText: 'Band Type option is only available for Tibbit #46',
                options: [
                    {
                        name: 'CAT-M',
                        value: '1',
                    },
                    {
                        name: 'NB-Iot',
                        value: '2',
                    },
                    {
                        name: 'CAT-M and NB-IoT',
                        value: '3',
                    },
                ],
            },
            {
                id: 'CELL__DEBUG_PRINT',
                name: 'Debug Print',
                settingType: SettingType.HIDDEN,
                required: true,
                helpText: 'Debug print messages',
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'CELL_LOG_LEVEL',
                name: 'Log Level',
                settingType: SettingType.OPTION,
                required: true,
                default: '2',
                helpText: `
**ERROR** - Only show error messages

**WARN** - Show warning and error messages

**INFO** - Show info, warning and error messages

**DEBUG** - Show debug, info, warning and error messages
                `,
                options: [
                    {
                        name: 'ERROR',
                        value: '0',
                    },
                    {
                        name: 'WARN',
                        value: '1',
                    },
                    {
                        name: 'INFO',
                        value: '2',
                    },
                    {
                        name: 'DEBUG',
                        value: '3',
                    },
                ],
            },

        ];
        this.settings = this.settings.concat(settings);
    }

    getDependencies() {
        return [
            'sock',
        ];
    }

    getFiles(): Array<string> {
        let files: Array<string> = [];
        if (this.getSettingValue('ENABLED') === '1') {
            files = [
                'libraries/cellular/cellular.tbs',
                'libraries/cellular/cellular.tbh',
            ];
        }
        return files;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.DISABLED;
        }
        const { project } = this;
        for (let i = 0; i < project.features.length; i += 1) {
            if (project.features[i].id === 'tibbit_45_1' || project.features[i].id === 'tibbit_45_2'
                || project.features[i].id === 'tibbit_45_3') {
                return settingId === 'CELL_BAND_TYPE' ? SettingState.HIDDEN : SettingState.ENABLED;
            }
        }

        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getDefines(): Array<Define> {
        const { project } = this;
        let socketNo: any = '0';
        let bandTypeDefine;
        for (let i = 0; i < project.features.length; i += 1) {
            if (project.features[i].id === 'tibbit_45_1' || project.features[i].id === 'tibbit_45_2'
                || project.features[i].id === 'tibbit_45_3' || project.features[i].id === 'tibbit_46') {
                socketNo = Tibbits.getSocket(project.features[i].getSettingValue('SOCKET'), this.project.device.id)?.serial || '0';
            }
            if (project.features[i].id === 'tibbit_46') {
                bandTypeDefine = `"${this.getSettingValue('CELL_BAND_TYPE')}"`;
            } else {
                bandTypeDefine = '"1"';
            }
        }
        const CELL__DEBUG_PRINT = this.getSettingValue('CELL__DEBUG_PRINT');


        let defines: Array<Define> = [];
        defines = defines.concat([{
            name: 'CELL_SER_PORT',
            value: socketNo,
        },
        {
            name: 'CELLULAR_AVAILABLE',
            value: '1',
        },
        {
            name: 'SUPPORTS_GPRS',
            value: '1',
        },
        {
            name: 'CELL_BANDTYPE',
            value: bandTypeDefine,
        },
        {
            name: 'SIGNAL_STRENGTH_INDICATION',
            value: '1',
        },
        {
            name: 'CELL_LOG_LEVEL',
            value: this.getSettingValue('CELL_LOG_LEVEL'),
        },
        ]);

        if (CELL__DEBUG_PRINT === '1') {
            defines.push({
                name: 'CELL__DEBUG_PRINT',
                value: '1',
            });
        }
        switch (this.getSettingValue('CELL_MODE')) {
            case 'CELL__PPP_MODE_ISENABLED':
                defines = defines.concat([{
                    name: 'CELL__PPP_MODE_ISENABLED',
                    value: '1',
                },
                {
                    name: 'CELL__AT_USERMODE_ISENABLED',
                    value: '0',
                },
                {
                    name: 'CELL__GPS_MODE_ISENABLED',
                    value: '0',
                },
                ]);
                break;
            case 'CELL__AT_USERMODE_ISENABLED':
                defines = defines.concat([{
                    name: 'CELL__PPP_MODE_ISENABLED',
                    value: '0',
                },
                {
                    name: 'CELL__AT_USERMODE_ISENABLED',
                    value: '1',
                },
                {
                    name: 'CELL__GPS_MODE_ISENABLED',
                    value: '0',
                },
                ]);
                break;
            case 'CELL__GPS_MODE_ISENABLED':
                defines = defines.concat([{
                    name: 'CELL__PPP_MODE_ISENABLED',
                    value: '0',
                },
                {
                    name: 'CELL__AT_USERMODE_ISENABLED',
                    value: '0',
                },
                {
                    name: 'CELL__GPS_MODE_ISENABLED',
                    value: '1',
                },
                ]);
                break;
            default:

                break;
        }
        return defines;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return blocks;
        }

        const CALLBACKS = [
            'cell__callback_modem_restart_failed',
            'cell__callback_modem_on',
            'cell__callback_modem_off',
            'cell__callback_insufficient_buffer_pages',
            'cell__callback_at_reply',
            'cell__callback_at_reply_contains_error',
            'cell__callback_at_storage_full',
        ];

        const PPP_CALLBACKS = [
            'cell__callback_ppp_fcs_is_bad',
            'cell__callback_ppp_missing_address_field',
            'cell__callback_ppp_missing_control_field',
            'cell__callback_ppp_lcp_msg_length_not_matching',
            'cell__callback_ppp_lcp_successful',
            'cell__callback_ppp_lcp_disreq_from_peer',
            'cell__callback_ppp_lcp_disreq_from_sender',
            'cell__callback_ppp_retry_exceeded',
            'cell__callback_ppp_pap_successful',
            'cell__callback_ppp_pap_nego_failed',
            'cell__callback_ppp_ipcp_successful',
            'cell__callback_ppp_connection_to_server_successful',
            'cell__callback_ppp_connection_to_server_retry',
            'cell__callback_ppp_connection_to_server_failed',
            'cell__callback_ppp_ipcp_disreq_from_peer',
            'cell__callback_ppp_ipcp_disreq_from_sender',
            'cell__callback_ppp_ping_successful',
            'cell__callback_ppp_ping_failed',
            'cell__check_http_conn_periodically',
            'cell__example_initiate_cmds_togo_from_PPP_to_CMD',
            'cell__example_run_at_cmds_after_switching_from_DATA_to_CMD',
            'cell__example_run_ppp_cmds_after_switching_from_CMD_to_DATA',
            'cell__callback_ppp_link_established',
            'cell__callback_after_switching_from_DATA_to_CMD',
            'cell__callback_after_switching_from_CMD_to_DATA',
        ];

        const USERMODE_CALLBACKS = [
            'cell__callback_at_notification',
            'cell__example_at_commands',
            'cell__example_send_sms',
            'cell__example_receive_sms',
            'cell__callback_at_usermode_sms_message_length_exceeded_error',
            'cell__callback_at_usermode_sms_sent_notif',
            'cell__callback_at_usermode_sms_rcv_notif',
            'cell__callback_at_usermode_sms_auto_reply',
            'cell__callback_at_usermode_sms_storage_full_notif',
        ];

        const GPS_CALLBACKS = [
            'cell__callback_gps_siv',
            'cell__callback_gps_position',
        ];
        if (this.getSettingValue('ENABLED').toString() === '0') {
            return blocks;
        }

        const { project } = this;
        let connectedSocket: any = '0';
        for (let i = 0; i < project.features.length; i += 1) {
            if (project.features[i].id === 'tibbit_45_1' || project.features[i].id === 'tibbit_45_2'
                || project.features[i].id === 'tibbit_45_3' || project.features[i].id === 'tibbit_46') {
                // this takes up two sockets, get the second one
                connectedSocket = (Number(project.features[i].getSettingValue('SOCKET')) + 2).toString();
            }
        }

        const CELL_APN = this.getSettingValue('CELL_APN');
        const CELL_UN = this.getSettingValue('CELL_UN');
        const CELL_PW = this.getSettingValue('CELL_PW');
        const CELL_PIN = this.getSettingValue('CELL_PIN');

        blocks.push({
            location: 'GLOBAL_DEFINES',
            blockType: BlockType.CODE,
            template: `
#define CELL_IO_SHUTDOWN ${Tibbits.getSocketPins(connectedSocket, this.project.device.id)[0]}
#define CELL_IO_PWR ${Tibbits.getSocketPins(connectedSocket, this.project.device.id)[1]}
#define CELL_IO_RESET ${Tibbits.getSocketPins(connectedSocket, this.project.device.id)[2]}
#define CELL_IO_STATUS ${Tibbits.getSocketPins(connectedSocket, this.project.device.id)[3]}
`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim CELL__AT_APN as string=""
dim CELL__MYUSERNAME as string=""
dim CELL__MYPASSWORD as string=""
dim CELL__SIMCARD_PIN as string=""

dim cell_running_light as low_high
dim cell_started as boolean=false
dim cell_signal_current_tick as dword=0
dim cell_sms_enabled as boolean=false
`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare cell_started as boolean
declare cell_sms_enabled as boolean

declare CELL__AT_APN as string
declare CELL__MYUSERNAME as string
declare CELL__MYPASSWORD as string
declare CELL__SIMCARD_PIN as string
`,
        });

        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            template: `
    cell_proc_data()
        `,
        });

        let wifiEnabledString = '';

        if (project.getFeature('wifi')?.getSettingValue('ENABLED') === '1') {
            wifiEnabledString = 'and wln_already_started = NO';
        }


        blocks.push({
            location: 'on_sys_timer',
            blockType: BlockType.CODE,
            template: `
    cell_proc_timer()

    if cell_started = true ${wifiEnabledString} AND sys_timercountms>cell_signal_current_tick then
        cell_signal_current_tick = sys_timercountms + 500
        if cell_running_light = LOW then
            signal_strength_set(&b10101)
            cell_running_light = HIGH
        else
            signal_strength_set(&b01010)
            cell_running_light = LOW
        end if
        if ppp.enabled = YES or cell_state = CELL_STATE_COMMAND_MODE then
            cell_started=false
            signal_strength_set(&b10101)
        end if
   end if
`,
        });

        blocks.push({
            location: 'boot_complete',
            blockType: BlockType.CODE,
            template: `
CELL__AT_APN = ${CELL_APN}
CELL__MYUSERNAME = ${CELL_UN}
CELL__MYPASSWORD = ${CELL_PW}
CELL__SIMCARD_PIN = ${CELL_PIN}
cell_init(CELL__AT_APN, CELL__MYUSERNAME, CELL__MYPASSWORD, CELL__SIMCARD_PIN)
cell_start()
cell_started=true
cell_ppp_enabled=true
  `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub callback_ppp_disconnected()
    '****callback_ppp_disconnected****'
    cell_started=true
    interface_set(PL_SOCK_INTERFACE_PPP,NO)
end sub

sub callback_ppp_connected()
    '****callback_ppp_connected****'
    interface_set(PL_SOCK_INTERFACE_PPP,YES)
end sub

sub callback_sms_received(phone_number as string, message as string)
    '****callback_sms_received****'
end sub
  `,
        });

        blocks.push({
            location: 'on_sock_event',
            blockType: BlockType.CODE,
            template: `
cell_proc_sock_event(newstate, newstatesimple)`,
        });

        return blocks;
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/tibbit_45';
    }

    validate(): Array<ValidationMessage> {
        const { project } = this;
        let instances_found = 0;
        const error_array = [];
        for (let i = 0; i < project.features.length; i += 1) {
            if (project.features[i].id === 'tibbit_45_1' || project.features[i].id === 'tibbit_45_2'
                || project.features[i].id === 'tibbit_45_3' || project.features[i].id === 'tibbit_46') {
                instances_found += 1;
            }
        }
        if (instances_found === 0) {
            error_array.push({
                message: 'A cellular Tibbit must be added to use cellular features',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
            });
        }
        if (instances_found > 1) {
            error_array.push({
                message: 'Only one cellular Tibbit can be used at a time',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
            });
        }
        return error_array;
    }
}
